import Editor from '@monaco-editor/react';
import {
	AlertCircle,
	ChevronDown,
	ChevronRight,
	Copy,
	Eye,
	EyeOff,
	HelpCircle,
	Loader2,
	Mail,
	Plus,
	Search,
	Send,
	Upload,
	X,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { useIsDark } from '../../contexts/ThemeContext';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { evaluateExpression } from '../../lib/expression-evaluator';
import { getStandardizedVariables } from '../../lib/variable-display-utils';
import type { EmailNodeConfig, IntegrationsNodeData } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { VariablePicker } from '../VariablePicker';

interface EmailConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
	onClose?: () => void; // Add close function prop
}

const defaultConfig: EmailNodeConfig = {
	from: { name: '', address: 'noreply@vquip.com' },
	to: [''], // Start with one empty recipient field
	cc: [],
	bcc: [],
	subject: '',
	template: '',
	htmlContent: '',
	templateFormat: 'html',
};

// Local VariableButton for inserting variables using new upstream logic
type VariableButtonProps = {
	fields: Array<{ id: string; label: string }>;
	onInsert: (variable: string) => void;
	className?: string;
};

function VariableButton({ fields, onInsert, className = '' }: VariableButtonProps) {
	const [showDropdown, setShowDropdown] = useState(false);

	if (!fields.length) return null;

	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setShowDropdown(!showDropdown)}
				className={`px-3 py-1 ml-2 text-sm font-medium text-blue-600 bg-blue-100 rounded transition-colors dark:text-blue-400 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 ${className}`}
				title="Insert variable"
			>
				Insert Variable
			</button>
			{showDropdown && (
				<div className="overflow-auto absolute z-10 mt-1 w-64 max-h-60 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-600">
					<div className="p-2 text-xs text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-600">
						Available variables:
					</div>
					{fields.map(field => (
						<button
							key={field.id}
							onClick={() => {
								onInsert(`{{${field.id}}}`);
								setShowDropdown(false);
							}}
							className="flex flex-col px-3 py-2 w-full text-left border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 last:border-b-0"
						>
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{field.label}
							</div>
							<div className="font-mono text-xs text-gray-500 dark:text-gray-400">
								{'{{' + field.id + '}}'}
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

// Helper to insert variable at cursor position - returns the new value instead of manipulating DOM
function insertAtCursor(
	value: string,
	insertText: string,
	cursorPosition?: number
): { newValue: string; newCursorPosition: number } {
	const start = cursorPosition || 0;
	const newValue = value.slice(0, start) + insertText + value.slice(start);
	const newCursorPosition = start + insertText.length;
	return { newValue, newCursorPosition };
}

export function EmailConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
	onClose,
}: EmailConfigProps) {
	const { getNodes, getEdges } = useReactFlow();
	const { getCurrentFlowId } = useWorkflowContext();
	const isDark = useIsDark();
	const editorTheme = isDark ? 'vs-dark' : 'vs-light';

	// Validation hooks
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();
	const {
		validationAttempted,
		validationFailed,
		performValidation,
		handleCompleteWizard,
		setValidationAttempted,
		setValidationFailed,
	} = useNodeValidationPattern(nodeId, data);

	// Helper function to normalize email recipients from string to array format
	const normalizeRecipients = (recipients: any): string[] => {
		if (!recipients) return [];

		// If already an array, return as is
		if (Array.isArray(recipients)) {
			return recipients;
		}

		// If it's a string, convert to array with single item
		if (typeof recipients === 'string') {
			return [recipients];
		}

		return [];
	};

	// All state declarations moved to top level
	const [config, setConfig] = useState<EmailNodeConfig>(() => {
		try {
			const existingConfig = data.config as Partial<EmailNodeConfig>;
			return {
				...defaultConfig,
				...existingConfig,
				to: normalizeRecipients(existingConfig?.to) || defaultConfig.to,
				cc: normalizeRecipients(existingConfig?.cc) || defaultConfig.cc,
				bcc: normalizeRecipients(existingConfig?.bcc) || defaultConfig.bcc,
			};
		} catch (error) {
			console.error('Error initializing email config:', error);
			return defaultConfig;
		}
	});

	const [showTokenLibrary, setShowTokenLibrary] = useState(false);
	const [tokenSearchTerm, setTokenSearchTerm] = useState('');
	// Wizard mode state - start in wizard mode by default
	const [wizardMode, setWizardMode] = useState(true);

	const [copiedToken, setCopiedToken] = useState<string | null>(null);
	const [insertedToken, setInsertedToken] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<'compose' | 'advanced' | 'preview' | 'test'>(
		'compose'
	);
	const [isWizard, setIsWizard] = useState(() => wizardMode);
	const [currentStep, setCurrentStep] = useState(1);

	// Keep isWizard in sync with wizardMode
	useEffect(() => {
		setIsWizard(wizardMode);
	}, [wizardMode]);
	const [isTestSending, setIsTestSending] = useState(false);
	const [testResult, setTestResult] = useState<string>('');
	const [showTestResult, setShowTestResult] = useState(false);
	const [hidePreview, setHidePreview] = useState(false);
	const [showRenderedHtml, setShowRenderedHtml] = useState(false);
	const [testEmail, setTestEmail] = useState('');
	const [editorKey, setEditorKey] = useState(0);
	const [showRawPreview, setShowRawPreview] = useState(false);
	const [previewHtml, setPreviewHtml] = useState('');
	const [recipientType, setRecipientType] = useState<'to' | 'cc' | 'bcc'>('to');
	const [showProTips, setShowProTips] = useState(true);
	const [showTokenPreview, setShowTokenPreview] = useState(false);

	// Refs
	const fromNameInputRef = useRef<HTMLInputElement>(null);
	const fromEmailInputRef = useRef<HTMLInputElement>(null);
	const subjectInputRef = useRef<HTMLInputElement>(null);
	const toEmailInputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);
	const editorRef = useRef<any>(null);
	
	// Store navigation handlers for direct access
	const navigationHandlersRef = useRef<{
		onPreviousStep: (() => void) | null;
		onNextStep: (() => void) | null;
	}>({ onPreviousStep: null, onNextStep: null });

	// Get available upstream fields using standardized utilities
	const availableFields = useMemo(() => {
		try {
			const nodes = getNodes();
			const edges = getEdges();

			if (!nodes || !Array.isArray(nodes)) {
				console.warn('No nodes available or nodes is not an array');
				return [];
			}

			if (!edges || !Array.isArray(edges)) {
				console.warn('No edges available or edges is not an array');
				return [];
			}

			// Use standardized utilities for consistent variable handling
			const standardizedVariables = getStandardizedVariables(nodeId, nodes, edges);

			// Convert to the expected format
			return standardizedVariables.map(variable => ({
				id: variable.cleanName, // Use clean name for display
				label: variable.field.label || variable.cleanName,
				description: variable.field.description,
				type: variable.field.type,
			}));
		} catch (error) {
			console.error('Error getting available fields:', error);
			return [];
		}
	}, [getNodes, getEdges, nodeId]);

	const flattenedFields = availableFields.map(field => ({
		...field,
		id: field.id,
		label: field.label,
		type: field.type,
		description: field.description || field.label,
	}));

	// Create mock context for preview
	const mockContext = flattenedFields.reduce((acc, field) => {
		acc[field.id] = getMockValue(field.type, field.label);
		return acc;
	}, {} as Record<string, any>);

	// Add some default mock values for common email fields
	const enhancedMockContext = {
		firstName: 'John',
		lastName: 'Doe',
		email: 'john.doe@example.com',
		companyName: 'Acme Corp',
		orderNumber: 'ORD-12345',
		orderTotal: '299.99',
		appointmentTime: '2:00 PM',
		appointmentDate: 'Tomorrow',
		// Add nested object structure for testing
		foo: {
			fullname: 'John Doe',
			email: 'john@foo.com',
			company: 'Foo Corp',
		},
		...mockContext,
	};

	// Helper function to resolve nested object properties using dot notation
	const resolveNestedValue = (obj: any, path: string): any => {
		return path.split('.').reduce((current, key) => {
			return current && current[key] !== undefined ? current[key] : undefined;
		}, obj);
	};

	// Helper function to navigate to a specific step
	const navigateToStep = (targetStep: number) => {
		console.log('Navigating to step:', targetStep, 'from current step:', currentStep);
		
		// Update local state first
		setCurrentStep(targetStep);
		
		// If we need to go backwards, call onPreviousStep multiple times
		if (targetStep < currentStep) {
			const stepsToGoBack = currentStep - targetStep;
			console.log('Need to go back', stepsToGoBack, 'steps');
			// We can't directly call the parent handlers, so we'll rely on the useEffect
			// to notify the parent of our step change
		}
		
		// If we need to go forwards, call onNextStep multiple times
		if (targetStep > currentStep) {
			const stepsToGoForward = targetStep - currentStep;
			console.log('Need to go forward', stepsToGoForward, 'steps');
			// We can't directly call the parent handlers, so we'll rely on the useEffect
			// to notify the parent of our step change
		}
	};

	function getMockValue(type: string, label: string): any {
		const lowerLabel = label.toLowerCase();

		// Smart mock values based on field names
		if (lowerLabel.includes('email')) return 'user@example.com';
		if (lowerLabel.includes('name')) return 'Sample Name';
		if (lowerLabel.includes('company')) return 'Sample Company';
		if (lowerLabel.includes('phone')) return '(555) 123-4567';
		if (lowerLabel.includes('address')) return '123 Main St, City, State 12345';
		if (lowerLabel.includes('date')) return '2024-01-15';
		if (lowerLabel.includes('time')) return '2:00 PM';
		if (lowerLabel.includes('price') || lowerLabel.includes('amount')) return '99.99';

		switch (type) {
			case 'string':
				return 'Sample Text';
			case 'number':
				return 42;
			case 'boolean':
				return true;
			case 'object':
				return { key: 'value' };
			case 'array':
				return ['item1', 'item2'];
			default:
				return 'Sample';
		}
	}

	// Update preview when config or context changes
	useEffect(() => {
		try {
			// Use htmlContent for custom templates, or show template name for predefined templates
			let rendered =
				config.htmlContent ||
				(config.template && !config.htmlContent
					? `[Using predefined template: ${config.template}]`
					: '');

			// Replace {{tokens}} with mock data only for custom content
			if (config.htmlContent) {
				// Handle both direct keys and nested dot notation
				Object.entries(enhancedMockContext).forEach(([key, value]) => {
					const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
					rendered = rendered.replace(regex, String(value));
				});

				// Handle nested properties like foo.fullname by extracting tokens from content
				const extractTokens = (text: string): string[] => {
					const tokenRegex = /\{\{([^}]+)\}\}/g;
					const tokens: string[] = [];
					let match;
					while ((match = tokenRegex.exec(text)) !== null) {
						if (!tokens.includes(match[1].trim())) {
							tokens.push(match[1].trim());
						}
					}
					return tokens;
				};

				const contentTokens = extractTokens(config.htmlContent);
				contentTokens.forEach(token => {
					const resolvedValue = resolveNestedValue(enhancedMockContext, token);
					if (resolvedValue !== undefined) {
						const regex = new RegExp(`\\{\\{${token}\\}\\}`, 'g');
						rendered = rendered.replace(regex, String(resolvedValue));
					}
				});
			}

			setPreviewHtml(rendered);
		} catch (error) {
			setPreviewHtml(
				'Preview error: ' + (error instanceof Error ? error.message : 'Unknown error')
			);
		}
	}, [config.template, config.htmlContent, enhancedMockContext]);

	const handleChange = (newConfig: Partial<EmailNodeConfig>) => {
		const updatedConfig = { ...config, ...newConfig };
		setConfig(updatedConfig); // Update local state so fields show changes
		onChange({
			...data,
			config: updatedConfig,
		});
	};

	// Feedback functions
	const copyTokenWithFeedback = (token: string) => {
		navigator.clipboard.writeText(token);
		setCopiedToken(token);
		setTimeout(() => setCopiedToken(null), 2000);
	};

	const insertTokenWithFeedback = (token: string, fieldName: string) => {
		setInsertedToken(`${token} into ${fieldName}`);
		setTimeout(() => setInsertedToken(null), 3000);
	};

	// Prepopulate function for development
	const handlePrepopulate = () => {
		const prepopulatedConfig: EmailNodeConfig = {
			from: {
				name: 'vQuip',
				address: 'noreply@vquip.com',
			},
			to: ['{{form-trigger-1.email}}'], // Fixed: use email field instead of last_name
			cc: [],
			bcc: [],
			subject: 'welcome {{form-trigger-1.company_name}}',
			template: '', // Empty for custom content
			htmlContent:
				'<p>Welcome {{form-trigger-1.first_name}} {{form-trigger-1.last_name}}</p>',
			templateFormat: 'html',
		};

		setConfig(prepopulatedConfig);
		handleChange(prepopulatedConfig);
	};

	// Wizard step definitions
	const wizardSteps = [
		{ id: 1, title: 'Compose Email', description: 'Set sender, recipients, and subject' },
		{ id: 2, title: 'Write Message Body', description: 'Create email content and template' },
		{ id: 3, title: 'Token Preview', description: 'Review all tokens and their values' },
		{ id: 4, title: 'Test Send', description: 'Send test email and verify setup' },
	];

	// Step validation logic
	const canProceedToStep = (step: number) => {
		const hasValidRecipients = config.to.some(email => email && email.trim() !== '');
		const hasValidContent = !!(config.template || config.htmlContent);

		switch (step) {
			case 1:
				return true; // Always can start
			case 2:
				return !!(config.from.address && hasValidRecipients && config.subject);
			case 3:
				return hasValidContent;
			case 4:
				return !!(
					config.from.address &&
					hasValidRecipients &&
					config.subject &&
					hasValidContent
				);
			default:
				return false;
		}
	};

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Communicate wizard state to parent component (BaseNode) so drawer footer shows prev/next buttons
	useEffect(() => {
		if (onWizardStateChange) {
			// Create navigation handlers
			const onPreviousStep = () => setCurrentStep(Math.max(1, currentStep - 1));
			const onNextStep = () => setCurrentStep(Math.min(4, currentStep + 1));
			
			// Store handlers in ref for direct access
			navigationHandlersRef.current = { onPreviousStep, onNextStep };
			
			onWizardStateChange({
				wizardMode: wizardMode, // Use the wizardMode state
				currentStep,
				totalSteps: 4, // Email wizard has 4 steps
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep,
				onNextStep,
				onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed

					// Save the current configuration
					const finalData = {
						...data,
						config,
					};
					onChange(finalData);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);

					// Close the drawer
					if (onClose) {
						onClose();
					}
				},
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		onWizardStateChange,
		currentStep,
		config,
		validationAttempted,
		validationFailed,
		validateNode,
		nodeId,
		data,
		onChange,
		setValidationAttempted,
		setValidationFailed,
		onClose,
		wizardMode,
	]);

	const addRecipient = (type: 'to' | 'cc' | 'bcc') => {
		const current = config[type] || [];
		handleChange({
			[type]: [...current, ''],
		});
	};

	const removeRecipient = (type: 'to' | 'cc' | 'bcc', index: number) => {
		const current = config[type] || [];
		const updated = [...current];
		updated.splice(index, 1);
		handleChange({ [type]: updated });
	};

	const updateRecipient = (type: 'to' | 'cc' | 'bcc', index: number, value: string) => {
		const current = config[type] || [];
		const updated = [...current];
		updated[index] = value;
		handleChange({ [type]: updated });
	};

	const addAttachment = () => {
		const current = config.attachments || [];
		handleChange({
			attachments: [...current, { name: '', url: '' }],
		});
	};

	const removeAttachment = (index: number) => {
		const current = config.attachments || [];
		const updated = [...current];
		updated.splice(index, 1);
		handleChange({ attachments: updated });
	};

	const updateAttachment = (index: number, field: 'name' | 'url', value: string) => {
		const current = config.attachments || [];
		const updated = [...current];
		updated[index] = { ...updated[index], [field]: value };
		handleChange({ attachments: updated });
	};

	const sendTestEmail = async () => {
		setIsTestSending(true);
		setTestResult('');

		try {
			// Get the current flow ID from the workflow context
			const flowId = getCurrentFlowId();

			// Prepare the test data with mock values for tokens
			const mockData = {
				...enhancedMockContext,
				// Add any additional mock data that might be needed
				executionId: 'test-execution-' + Date.now(),
				submittedBy: 'test-user',
				timestamp: new Date().toISOString(),
			};

			let response;

			if (flowId) {
				// Use the workflow-specific endpoint if we have a flow ID
				response = await fetch(`/api/v1/composer/flows/${flowId}/test-email`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						nodeId: nodeId,
						testRecipient: testEmail,
						mockData: mockData,
					}),
				});
			} else {
				// Use the direct email config testing endpoint if no flow ID
				response = await fetch(`/api/v1/composer/flows/test-email-config`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						emailConfig: config,
						testRecipient: testEmail,
						mockData: mockData,
					}),
				});
			}

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					errorData.message || `HTTP ${response.status}: ${response.statusText}`
				);
			}

			const result = await response.json();

			if (result.success) {
				setTestResult('Success: Test email sent successfully! Check your inbox.');
			} else {
				setTestResult('Failed to send test email: ' + result.message);
			}
		} catch (error) {
			console.error('Test email error:', error);
			setTestResult(
				'Failed to send test email: ' +
					(error instanceof Error ? error.message : 'Unknown error')
			);
		} finally {
			setIsTestSending(false);
		}
	};

	// Handle template format changes
	const handleFormatChange = (newFormat: 'html' | 'plaintext') => {
		// Store current content and cursor position
		let currentContent = config.template;
		let cursorPosition = { lineNumber: 1, column: 1 };

		if (editorRef.current) {
			currentContent = editorRef.current.getModel()?.getValue() || config.template;
			cursorPosition = editorRef.current.getPosition() || { lineNumber: 1, column: 1 };
		}

		// Update the format
		handleChange({ templateFormat: newFormat });

		// Force editor re-mount with new key
		setEditorKey(prev => prev + 1);

		// Clear the editor reference since it will be recreated
		editorRef.current = null;
	};

	const renderRecipientField = (type: 'to' | 'cc' | 'bcc', label: string, required = false) => (
		<div className="space-y-2">
			<label className="block text-sm font-medium text-gray-700">
				{label}
				{required && <span className="text-red-500">*</span>}
			</label>
			{(config[type] || []).map((recipient, index) => (
				<div key={index} className="flex gap-2 items-center">
					<VariablePicker
						nodeId={nodeId}
						value={recipient}
						onChange={value => updateRecipient(type, index, value)}
						placeholder={`Enter email or {{variable}} (e.g., {{${type}_email}})`}
						className="w-full text-sm"
						ref={el => (toEmailInputRefs.current[index] = el)}
					/>
					<VariableButton
						fields={flattenedFields}
						onInsert={variable => {
							const input = toEmailInputRefs.current[index];
							if (input) {
								const cursorPos = input.selectionStart || 0;
								const { newValue } = insertAtCursor(recipient, variable, cursorPos);
								updateRecipient(type, index, newValue);
								insertTokenWithFeedback(variable, 'Recipient');
							}
						}}
					/>
					<button
						onClick={() => removeRecipient(type, index)}
						className="p-1 text-red-500 rounded hover:text-red-700 hover:bg-red-50"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
			))}
		</div>
	);

	// Enhanced Token Helper Panel Component
	const TokenHelperPanel = ({
		onInsert,
	}: {
		onInsert: (token: string, fieldType?: string) => void;
	}) => {
		const [activeGroup, setActiveGroup] = useState<string | null>(null);

		// Group available fields by their source/context with enhanced categorization
		const groupedFields = availableFields.reduce((groups, field) => {
			const parts = field.id.split('.');
			const prefix = parts[0];
			let groupName = '';
			let groupIcon = '';
			let groupColor = '';

			if (prefix === 'form' || prefix === 'submissionForm') {
				groupName = '📝 Form Fields';
				groupIcon = '📝';
				groupColor = 'green';
			} else if (prefix === 'user') {
				groupName = '👤 User Info';
				groupIcon = '👤';
				groupColor = 'blue';
			} else if (prefix === 'execution') {
				groupName = '⚙️ Execution Info';
				groupIcon = '⚙️';
				groupColor = 'gray';
			} else if (
				prefix.includes('api') ||
				prefix.includes('response') ||
				parts.includes('data')
			) {
				groupName = '🔗 API Responses';
				groupIcon = '🔗';
				groupColor = 'purple';
			} else if (
				prefix.includes('transform') ||
				prefix.includes('userData') ||
				prefix.includes('processedData')
			) {
				groupName = '🔄 Transform Outputs';
				groupIcon = '🔄';
				groupColor = 'orange';
			} else {
				groupName = '📋 Other Data';
				groupIcon = '📋';
				groupColor = 'gray';
			}

			if (!groups[groupName]) {
				groups[groupName] = { fields: [], icon: groupIcon, color: groupColor };
			}
			groups[groupName].fields.push(field);
			return groups;
		}, {} as Record<string, { fields: typeof availableFields; icon: string; color: string }>);

		const getColorClasses = (color: string) => {
			const colors = {
				green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
				blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
				purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300',
				orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300',
				gray: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300',
			};
			return colors[color as keyof typeof colors] || colors.gray;
		};

		if (availableFields.length === 0) {
			return (
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
					<div className="text-center">
						<div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-gray-100 rounded-full dark:bg-gray-600">
							<span className="text-xl">🔌</span>
						</div>
						<h4 className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
							No Data Available
						</h4>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							Connect this email node to a form, API call, or other data source to see
							available tokens.
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className="overflow-hidden bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-600">
				<div className="p-3 bg-gray-50 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-600">
					<h4 className="flex gap-2 items-center text-sm font-medium text-gray-900 dark:text-white">
						<span className="text-lg">🎯</span>
						Available Tokens ({availableFields.length})
					</h4>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						Click any token to insert it into the active field
					</p>
				</div>

				<div className="overflow-y-auto max-h-80">
					{Object.entries(groupedFields).map(([groupName, groupData]) => (
						<div
							key={groupName}
							className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
						>
							<button
								onClick={() =>
									setActiveGroup(activeGroup === groupName ? null : groupName)
								}
								className={`w-full p-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
									activeGroup === groupName ? 'bg-gray-50 dark:bg-gray-700' : ''
								}`}
							>
								<div className="flex gap-2 items-center">
									<span className="text-sm">{groupData.icon}</span>
									<span className="text-sm font-medium text-gray-900 dark:text-white">
										{groupName}
									</span>
									<span
										className={`px-2 py-0.5 text-xs rounded-full border ${getColorClasses(
											groupData.color
										)}`}
									>
										{groupData.fields.length}
									</span>
								</div>
								<span
									className={`text-gray-400 transform transition-transform ${
										activeGroup === groupName ? 'rotate-90' : ''
									}`}
								>
									▶
								</span>
							</button>

							{activeGroup === groupName && (
								<div className="bg-gray-50 dark:bg-gray-700">
									{groupData.fields.map(field => (
										<button
											key={field.id}
											onClick={() => onInsert(`{{${field.id}}}`, 'token')}
											className="p-3 w-full text-left border-t border-gray-100 hover:bg-white dark:hover:bg-gray-800 dark:border-gray-700 group"
										>
											<div className="flex justify-between items-start">
												<div className="flex-1 min-w-0">
													<div className="flex gap-2 items-center mb-1">
														<code className="font-mono text-sm font-medium text-purple-600 dark:text-purple-400">
															{`{{${field.id}}}`}
														</code>
														<Copy className="w-3 h-3 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
													</div>
													<div className="mb-1 text-xs text-gray-600 dark:text-gray-400">
														<span className="font-medium">
															{field.label}
														</span>
														{field.description && (
															<span className="ml-1 text-gray-500 dark:text-gray-500">
																• {field.description}
															</span>
														)}
													</div>
													<div className="inline-block px-2 py-1 font-mono text-xs text-gray-700 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-300">
														Preview:{' '}
														<span className="text-green-600 dark:text-green-400">
															{(enhancedMockContext as any)[
																field.id
															] || 'No preview'}
														</span>
													</div>
												</div>
											</div>
										</button>
									))}
								</div>
							)}
						</div>
					))}
				</div>

				<div className="p-3 bg-blue-50 border-t border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<div className="text-xs text-blue-700 dark:text-blue-300">
						<strong>💡 Tip:</strong> Tokens will be replaced with real data when the
						workflow runs. Preview values help you understand what each token contains.
					</div>
				</div>
			</div>
		);
	};

	// Tooltip Component
	const Tooltip = ({ content, children }: { content: string; children: React.ReactNode }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		return (
			<div className="inline-flex relative items-center">
				{children}
				<button
					type="button"
					onMouseEnter={() => setShowTooltip(true)}
					onMouseLeave={() => setShowTooltip(false)}
					className="ml-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
				>
					<HelpCircle className="w-4 h-4" />
				</button>
				{showTooltip && (
					<div className="absolute left-0 bottom-full z-50 p-2 mb-1 w-64 text-xs text-white bg-gray-900 rounded-md shadow-lg dark:bg-gray-700 dark:text-gray-100">
						{content}
						<div className="absolute left-2 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
					</div>
				)}
			</div>
		);
	};

	// Enhanced Token Picker Component (for smaller inline use)
	const TokenPicker = ({
		onInsert,
		className = '',
	}: {
		onInsert: (token: string) => void;
		className?: string;
	}) => {
		const [showDropdown, setShowDropdown] = useState(false);
		const dropdownRef = useRef<HTMLDivElement>(null);

		// Close dropdown when clicking outside
		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
					setShowDropdown(false);
				}
			};

			if (showDropdown) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}, [showDropdown]);

		// Safety check for availableFields
		if (!availableFields || availableFields.length === 0) {
			return null;
		}

		const handleTokenClick = (token: string) => {
			try {
				onInsert(token);
				setShowDropdown(false);
			} catch (error) {
				console.error('Error inserting token:', error);
				setShowDropdown(false);
			}
		};

		return (
			<div className="relative" ref={dropdownRef}>
				<button
					type="button"
					onClick={() => setShowDropdown(!showDropdown)}
					className={`px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-md transition-colors ${
						showDropdown
							? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-600'
							: 'bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50'
					} ${className}`}
					title="Insert token from upstream data"
				>
					+ Insert Token {showDropdown && '▼'}
				</button>

				{showDropdown && (
					<div className="absolute z-[100] mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl max-h-96 overflow-hidden right-0">
						<div className="p-3 bg-gray-50 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-600">
							<h4 className="text-sm font-medium text-gray-900 dark:text-white">
								Available Tokens
							</h4>
							<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
								Click to insert at cursor position
							</p>
						</div>

						<div className="overflow-y-auto max-h-60">
							{availableFields.slice(0, 8).map(field => (
								<button
									key={field.id}
									type="button"
									onClick={e => {
										e.stopPropagation();
										handleTokenClick(`{{${field.id}}}`);
									}}
									className="flex justify-between items-center px-3 py-2 w-full text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 group"
								>
									<div className="flex-1 min-w-0">
										<div className="flex gap-2 items-center">
											<code className="font-mono text-sm font-medium text-purple-600 dark:text-purple-400">
												{`{{${field.id}}}`}
											</code>
											<Copy className="w-3 h-3 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
										</div>
										<div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
											<span className="font-medium">{field.label}</span>
											{field.description && (
												<span className="ml-1 text-gray-500 dark:text-gray-500">
													• {field.description}
												</span>
											)}
										</div>
										<div className="text-xs text-green-600 dark:text-green-400 font-mono mt-0.5">
											Preview:{' '}
											{(() => {
												try {
													return (
														(enhancedMockContext as any)[field.id] ||
														'No preview'
													);
												} catch (error) {
													return 'Error loading preview';
												}
											})()}
										</div>
									</div>
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderStep1ComposeEmail = () => (
		<div className="space-y-8">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">Compose Email</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Set sender, recipients, and subject before composing content.
				</p>
			</div>

			{/* Copy feedback toast */}
			{copiedToken && (
				<div className="flex fixed top-4 right-4 z-50 gap-2 items-center px-4 py-2 text-green-800 bg-green-100 rounded-lg border border-green-200 shadow-lg dark:bg-green-900 dark:border-green-700 dark:text-green-200">
					<span className="text-sm">📋</span>
					<span className="text-sm font-medium">Copied {copiedToken} to clipboard!</span>
				</div>
			)}

			{/* Insert feedback toast */}
			{insertedToken && (
				<div className="flex fixed right-4 top-16 z-50 gap-2 items-center px-4 py-2 text-blue-800 bg-blue-100 rounded-lg border border-blue-200 shadow-lg dark:bg-blue-900 dark:border-blue-700 dark:text-blue-200">
					<span className="text-sm">✅</span>
					<span className="text-sm font-medium">Inserted {insertedToken}!</span>
				</div>
			)}

			{/* Token Library - Collapsible with Search */}
			{availableFields.length > 0 && (
				<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
					<button
						onClick={() => setShowTokenLibrary(!showTokenLibrary)}
						className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
					>
						<div className="flex items-center gap-2">
							<span className="text-lg">🎯</span>
							<span className="text-sm font-medium text-gray-900 dark:text-white">
								Token Library ({availableFields.length} available)
							</span>
						</div>
						{showTokenLibrary ? (
							<ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
						) : (
							<ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
						)}
					</button>

					{showTokenLibrary && (
						<div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
							{/* Search Input */}
							<div className="mb-4">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
									<input
										type="text"
										value={tokenSearchTerm}
										onChange={e => setTokenSearchTerm(e.target.value)}
										placeholder="Search tokens..."
										className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
										aria-label="Search available tokens"
									/>
								</div>
							</div>

							{/* Filtered Token Pills */}
							<div className="flex flex-wrap gap-2">
								{flattenedFields
									.filter(
										field =>
											!tokenSearchTerm ||
											field.label
												.toLowerCase()
												.includes(tokenSearchTerm.toLowerCase()) ||
											field.id
												.toLowerCase()
												.includes(tokenSearchTerm.toLowerCase())
									)
									.slice(0, 12)
									.map(field => (
										<button
											key={field.id}
											onClick={() => copyTokenWithFeedback(`{{${field.id}}}`)}
											className="inline-flex gap-1 items-center px-3 py-1.5 font-mono text-xs text-blue-700 bg-blue-100 rounded-md transition-colors group dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/60 border border-blue-200 dark:border-blue-700"
											title={`Click to copy {{${field.id}}} • Preview: ${
												(enhancedMockContext as any)[field.id] ||
												'No preview'
											}`}
										>
											{`{{${field.id}}}`}
											<Copy className="w-3 h-3 opacity-0 transition-opacity group-hover:opacity-100" />
										</button>
									))}
								{flattenedFields.filter(
									field =>
										!tokenSearchTerm ||
										field.label
											.toLowerCase()
											.includes(tokenSearchTerm.toLowerCase()) ||
										field.id
											.toLowerCase()
											.includes(tokenSearchTerm.toLowerCase())
								).length > 12 && (
									<span className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">
										+
										{flattenedFields.filter(
											field =>
												!tokenSearchTerm ||
												field.label
													.toLowerCase()
													.includes(tokenSearchTerm.toLowerCase()) ||
												field.id
													.toLowerCase()
													.includes(tokenSearchTerm.toLowerCase())
										).length - 12}{' '}
										more
									</span>
								)}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Development Helper - Reduced Visual Weight */}
			{import.meta.env.DEV && (
				<div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
					<div className="flex gap-3 items-center">
						<div className="flex flex-shrink-0 justify-center items-center w-6 h-6 bg-gray-200 rounded dark:bg-gray-600">
							<span className="text-xs">🚀</span>
						</div>
						<div className="flex-1">
							<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
								Development Helper
							</h4>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								Fills fields with sample data for faster dev testing. UI-only.
							</p>
						</div>
						<button
							onClick={handlePrepopulate}
							title="Fills fields with sample data for faster dev testing. UI-only."
							className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md transition-colors hover:bg-gray-50 dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
						>
							Prepopulate test values
						</button>
					</div>
				</div>
			)}

			{/* Section Divider */}
			<div className="border-t border-gray-200 dark:border-gray-700"></div>

			{/* From Fields */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div className="space-y-2">
					<Tooltip content="Can use a static string or a token like {{user.name}} for dynamic sender names.">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							From Name
						</label>
					</Tooltip>
					<div className="flex gap-2 items-center">
						<VariablePicker
							nodeId={nodeId}
							value={config.from.name}
							onChange={value =>
								handleChange({ from: { ...config.from, name: value } })
							}
							placeholder="Your Name or {{sender_name}}"
							className="flex-1 min-w-0"
							ref={fromNameInputRef}
						/>
						<TokenPicker
							onInsert={token => {
								// Instead of always appending, replace the current value if it's empty or append
								const currentValue = config.from.name || '';
								const newValue = currentValue ? currentValue + token : token;
								handleChange({ from: { ...config.from, name: newValue } });
								insertTokenWithFeedback(token, 'From Name');
							}}
						/>
					</div>
					{config.from.name && config.from.name.includes('{{') && (
						<div className="p-2 text-xs text-gray-500 bg-gray-50 rounded border dark:text-gray-400 dark:bg-gray-700">
							<strong>Preview:</strong>{' '}
							{evaluateExpression(config.from.name, enhancedMockContext)}
						</div>
					)}
				</div>

				<div className="space-y-2">
					<Tooltip content="Currently only noreply@vquip.com is supported as the From email address.">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							From Email <span className="text-red-500 dark:text-red-400">*</span>
						</label>
					</Tooltip>
					<div className="flex gap-2 items-center">
						<VariablePicker
							nodeId={nodeId}
							value={config.from.address}
							onChange={value =>
								handleChange({ from: { ...config.from, address: value } })
							}
							placeholder="noreply@vquip.com (only supported address)"
							className="flex-1 min-w-0"
							ref={fromEmailInputRef}
						/>
						<TokenPicker
							onInsert={token => {
								const currentValue = config.from.address || '';
								const newValue = currentValue ? currentValue + token : token;
								handleChange({ from: { ...config.from, address: newValue } });
								insertTokenWithFeedback(token, 'From Email');
							}}
						/>
					</div>
					{config.from.address && config.from.address.includes('{{') && (
						<div className="p-2 text-xs text-gray-500 bg-gray-50 rounded border dark:text-gray-400 dark:bg-gray-700">
							<strong>Preview:</strong>{' '}
							{evaluateExpression(config.from.address, enhancedMockContext)}
						</div>
					)}
				</div>
			</div>

			{/* Recipients */}
			<div className="space-y-4">
				<div className="space-y-3">
					<Tooltip content="Add static or dynamic recipients. Use tokens like {{form.email}} to send to form submitters.">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							To Recipients <span className="text-red-500 dark:text-red-400">*</span>
						</label>
					</Tooltip>
					{(config.to || []).map((email, index) => (
						<div key={index} className="space-y-2">
							<div className="flex gap-2 items-center">
								<VariablePicker
									nodeId={nodeId}
									value={email}
									onChange={value => updateRecipient('to', index, value)}
									placeholder="recipient@example.com or {{user_email}}"
									className="flex-1 min-w-0"
								/>
								<TokenPicker
									onInsert={token => {
										const currentValue = email || '';
										const newValue = currentValue
											? currentValue + token
											: token;
										updateRecipient('to', index, newValue);
										insertTokenWithFeedback(token, 'Recipient');
									}}
								/>
								<button
									onClick={() => removeRecipient('to', index)}
									className="p-2 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
								>
									<X className="w-4 h-4" />
								</button>
							</div>
							{email && email.includes('{{') && (
								<div className="p-2 ml-0 text-xs text-gray-500 bg-gray-50 rounded border dark:text-gray-400 dark:bg-gray-700">
									<strong>Preview:</strong>{' '}
									{evaluateExpression(email, enhancedMockContext)}
								</div>
							)}
						</div>
					))}
					<button
						onClick={() => addRecipient('to')}
						className="flex gap-1 items-center px-3 py-2 text-sm text-green-600 bg-green-50 rounded-md border border-green-200 transition-colors dark:text-green-400 dark:bg-green-900/30 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50"
					>
						<Plus className="w-4 h-4" />
						Add To Recipient
					</button>
				</div>
			</div>

			{/* Subject */}
			<div className="space-y-2">
				<Tooltip content="Use tokens like {{form.first_name}} or {{invoice.data.total}} to personalize the subject line.">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Subject <span className="text-red-500 dark:text-red-400">*</span>
					</label>
				</Tooltip>
				<div className="flex gap-2 items-center">
					<VariablePicker
						nodeId={nodeId}
						value={config.subject}
						onChange={value => handleChange({ subject: value })}
						placeholder="Email subject with {{variables}}"
						className="flex-1 min-w-0"
						ref={subjectInputRef}
					/>
					<TokenPicker
						onInsert={token => {
							const currentValue = config.subject || '';
							const newValue = currentValue ? currentValue + token : token;
							handleChange({ subject: newValue });
						}}
					/>
				</div>
				{config.subject && (
					<div className="p-3 text-sm text-gray-700 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 dark:text-gray-300 dark:from-green-900/20 dark:to-blue-900/20 dark:border-green-800">
						<div className="flex gap-2 items-center mb-1">
							<span className="text-xs font-medium tracking-wide text-green-700 uppercase dark:text-green-300">
								Preview
							</span>
							<div className="w-1 h-1 bg-green-400 rounded-full"></div>
						</div>
						<div className="font-medium text-gray-900 dark:text-white">
							{evaluateExpression(config.subject, enhancedMockContext)}
						</div>
					</div>
				)}
			</div>

			{/* Validation Status */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					✅ Step 1 Checklist
				</h4>
				<div className="space-y-2 text-sm">
					<div className="flex gap-2 items-center">
						<div
							className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
								config.from.address
									? 'bg-green-500 text-white'
									: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
							}`}
						>
							{config.from.address ? '✓' : '1'}
						</div>
						<span
							className={
								config.from.address
									? 'text-green-700 dark:text-green-300'
									: 'text-gray-600 dark:text-gray-400'
							}
						>
							From Email configured
						</span>
					</div>
					<div className="flex gap-2 items-center">
						<div
							className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
								config.to.some(email => email && email.trim() !== '')
									? 'bg-green-500 text-white'
									: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
							}`}
						>
							{config.to.some(email => email && email.trim() !== '') ? '✓' : '2'}
						</div>
						<span
							className={
								config.to.some(email => email && email.trim() !== '')
									? 'text-green-700 dark:text-green-300'
									: 'text-gray-600 dark:text-gray-400'
							}
						>
							At least one recipient added
						</span>
					</div>
					<div className="flex gap-2 items-center">
						<div
							className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
								config.subject
									? 'bg-green-500 text-white'
									: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
							}`}
						>
							{config.subject ? '✓' : '3'}
						</div>
						<span
							className={
								config.subject
									? 'text-green-700 dark:text-green-300'
									: 'text-gray-600 dark:text-gray-400'
							}
						>
							Subject line written
						</span>
					</div>
				</div>
			</div>
		</div>
	);

	const renderStep2WriteMessageBody = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					Write Message Body
				</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Create your email content with dynamic token support
				</p>
			</div>

			{/* Template Mode Selection */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<label className="block mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
					Email Content Type
				</label>
				<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
					<button
						type="button"
						onClick={() => {
							if (config.template) {
								// Switching from predefined to custom - move template to htmlContent if it contains HTML
								handleChange({
									template: '',
									htmlContent: config.template.includes('<')
										? config.template
										: config.htmlContent || '',
								});
							}
						}}
						className={`p-3 text-left border rounded-lg transition-colors ${
							!config.template || config.htmlContent
								? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
								: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
						}`}
					>
						<div className="font-medium text-gray-900 dark:text-white">
							Custom HTML Content
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Write your own HTML email template
						</div>
					</button>

					<button
						type="button"
						onClick={() => {
							// Switching to predefined template mode
							handleChange({
								template: 'genericWorkflowTemplate',
								htmlContent: '',
							});
						}}
						className={`p-3 text-left border rounded-lg transition-colors ${
							config.template && !config.htmlContent
								? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
								: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
						}`}
					>
						<div className="font-medium text-gray-900 dark:text-white">
							Predefined Template
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Use a predefined email template
						</div>
					</button>
				</div>
			</div>

			{/* Template Selection UX - Grid of template thumbnails */}
			{config.template && !config.htmlContent && (
				<div className="space-y-3">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Predefined Template{' '}
						<span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						{[
							{
								value: 'forgotPasswordTemplate',
								name: 'Forgot Password',
								description: 'Password reset email with clear instructions',
							},
							{
								value: 'bdxExportTemplate',
								name: 'BDX Export',
								description: 'Data export confirmation and download link',
							},
							{
								value: 'stripeFailureTemplate',
								name: 'Stripe Failure',
								description: 'Payment failure notification with retry options',
							},
							{
								value: 'genericWorkflowTemplate',
								name: 'Generic Workflow',
								description: 'Standard workflow notification template',
							},
						].map(template => (
							<button
								key={template.value}
								onClick={() => handleChange({ template: template.value })}
								className={`p-4 text-left border rounded-lg transition-colors ${
									config.template === template.value
										? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
								}`}
							>
								<div className="font-medium text-gray-900 dark:text-white mb-1">
									{template.name}
								</div>
								<div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
									{template.description}
								</div>
								<div className="flex gap-2">
									<button
										type="button"
										onClick={e => {
											e.stopPropagation();
											// Preview template (UI only)
										}}
										className="px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded border border-blue-300 transition-colors dark:text-blue-300 dark:bg-blue-900/40 dark:border-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900/60"
									>
										👁️ Preview
									</button>
								</div>
							</button>
						))}
					</div>
				</div>
			)}

			{/* Editor Mode & Placement - moved above editor */}
			{(!config.template || config.htmlContent) && (
				<div className="space-y-3">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Editor Mode
					</label>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => handleFormatChange('html')}
							className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
								config.templateFormat === 'html'
									? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:border-blue-400 dark:text-blue-300'
									: 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
							}`}
						>
							Rich HTML
						</button>
						<button
							type="button"
							onClick={() => handleFormatChange('plaintext')}
							className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
								config.templateFormat === 'plaintext'
									? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:border-blue-400 dark:text-blue-300'
									: 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
							}`}
						>
							Plain Text
						</button>
					</div>
				</div>
			)}

			{/* Token Helper - Collapsible Quick Token Insert */}
			{availableFields.length > 0 && (
				<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
					<button
						onClick={() => setShowTokenLibrary(!showTokenLibrary)}
						className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
					>
						<div className="flex items-center gap-2">
							<span className="text-lg">🎯</span>
							<span className="text-sm font-medium text-gray-900 dark:text-white">
								Quick Token Insert ({availableFields.length} available)
							</span>
						</div>
						{showTokenLibrary ? (
							<ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
						) : (
							<ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
						)}
					</button>

					{showTokenLibrary && (
						<div className="p-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
							<div className="flex flex-wrap gap-1">
								{flattenedFields.slice(0, 8).map(field => (
									<button
										key={field.id}
										onClick={() => {
											if (editorRef.current) {
												const model = editorRef.current.getModel();
												const position = editorRef.current.getPosition();
												const value = model.getValue();
												const offset = model.getOffsetAt(position);
												const variable = `{{${field.id}}}`;
												const newValue =
													value.slice(0, offset) +
													variable +
													value.slice(offset);
												model.setValue(newValue);
												editorRef.current.setPosition({
													...position,
													column: position.column + variable.length,
												});
												editorRef.current.focus();
											}
										}}
										className="px-2 py-1 text-xs text-green-700 bg-white rounded border border-green-300 transition-colors dark:text-green-300 dark:bg-gray-700 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
										title={`Insert {{${field.id}}} - ${
											field.description || field.label
										}`}
									>
										{`{{${field.label}}}`}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Template Content Editor */}
			<div className="space-y-4">
				{config.template && !config.htmlContent ? (
					// Predefined Template Mode - already handled above
					<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
						<p className="text-sm text-blue-800 dark:text-blue-200">
							<strong>Using predefined template:</strong> {config.template}
						</p>
						<p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
							This template is managed by the system and will automatically include
							the appropriate content and styling. You can still use tokens like{' '}
							{'{{form-trigger-1.first_name}}'} in the subject line and recipient
							fields.
						</p>
					</div>
				) : (
					// Custom HTML Content Mode
					<>
						<div className="flex justify-between items-center">
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
								Custom Email Content{' '}
								<span className="text-red-500 dark:text-red-400">*</span>
							</label>
							<div className="flex gap-2">
								<VariableButton
									fields={flattenedFields}
									onInsert={variable => {
										if (editorRef.current) {
											const model = editorRef.current.getModel();
											const position = editorRef.current.getPosition();
											const value = model.getValue();
											const offset = model.getOffsetAt(position);
											const newValue =
												value.slice(0, offset) +
												variable +
												value.slice(offset);
											model.setValue(newValue);
											editorRef.current.setPosition({
												...position,
												column: position.column + variable.length,
											});
											editorRef.current.focus();
										}
									}}
								/>
								<button
									type="button"
									onClick={() => {
										// AI Assist - UI only hook
									}}
									className="px-3 py-1.5 text-sm text-purple-600 bg-purple-100 rounded-lg border border-purple-300 transition-colors dark:text-purple-300 dark:bg-purple-900/40 dark:border-purple-600 hover:bg-purple-200 dark:hover:bg-purple-900/60"
									title="Get AI suggestions for email content"
								>
									✨ Suggest Body
								</button>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
							{/* Editor */}
							<div className="space-y-3 lg:col-span-2">
								<div className="overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600 ring-1 ring-gray-200 dark:ring-gray-700">
									<Editor
										key={editorKey}
										height="400px"
										language={
											config.templateFormat === 'html' ? 'html' : 'plaintext'
										}
										value={config.htmlContent || ''}
										onChange={value =>
											handleChange({ htmlContent: value || '' })
										}
										options={{
											minimap: { enabled: false },
											lineNumbers: 'on',
											scrollBeyondLastLine: false,
											wordWrap: 'on',
											wrappingIndent: 'indent',
											automaticLayout: true,
											formatOnPaste: true,
										}}
										theme={editorTheme}
										onMount={editor => (editorRef.current = editor)}
									/>
								</div>

								{/* Guidance & Microcopy */}
								<div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
									<p>
										Use{' '}
										<code className="px-1 bg-gray-100 rounded dark:bg-gray-700">
											{'{{variable_name}}'}
										</code>{' '}
										to insert dynamic values.
									</p>
									<p>
										Remember to keep content accessible; avoid image‑only
										emails.
									</p>
									{!config.htmlContent && (
										<p className="text-amber-600 dark:text-amber-400">
											💡 Add some content to enable a meaningful preview.
										</p>
									)}
								</div>
							</div>

							{/* Right Column - Live Preview + Checklist */}
							<div className="space-y-4">
								{/* Live Preview - Enhanced */}
								<div className="space-y-3">
									<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
										📧 Live Preview
									</h4>
									<div className="overflow-hidden bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700">
										{/* Header strip with From/To/Subject */}
										<div className="p-3 text-sm bg-gray-50 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-600">
											<div className="space-y-1 text-gray-900 dark:text-gray-100">
												<div>
													<strong>From:</strong>{' '}
													{config.from.name || 'Not set'} &lt;
													{config.from.address || 'Not set'}&gt;
												</div>
												<div>
													<strong>To:</strong>{' '}
													{config.to.length > 0
														? config.to.join(', ')
														: 'No recipients'}
												</div>
												<div>
													<strong>Subject:</strong>{' '}
													{config.subject
														? evaluateExpression(
																config.subject,
																enhancedMockContext
														  )
														: 'Not set'}
												</div>
											</div>
											{(!config.from.name ||
												!config.from.address ||
												config.to.length === 0 ||
												!config.subject) && (
												<div className="mt-2 p-2 text-xs text-amber-700 bg-amber-50 rounded border border-amber-200 dark:text-amber-300 dark:bg-amber-900/20 dark:border-amber-800">
													💡 Some header values are missing. Complete Step
													1 for a full preview.
												</div>
											)}
										</div>

										{/* Body area with enhanced styling */}
										<div className="overflow-auto p-4 max-h-64 min-h-64">
											{config.htmlContent ? (
												config.templateFormat === 'html' ? (
													<div
														className="max-w-none prose dark:prose-invert prose-sm leading-relaxed"
														dangerouslySetInnerHTML={{
															__html: previewHtml,
														}}
													/>
												) : (
													<div className="font-mono text-sm text-gray-700 whitespace-pre-wrap dark:text-gray-300 leading-relaxed">
														{previewHtml}
													</div>
												)
											) : (
												<div className="text-center text-gray-500 dark:text-gray-400 py-8">
													📝 No content to preview
												</div>
											)}
										</div>
									</div>
								</div>

								{/* Step 2 Checklist - Desktop sticky version */}
								<div className="lg:block">
									<div className="p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
										<h4 className="mb-3 text-sm font-medium text-green-900 dark:text-green-200">
											✅ Step 2 Checklist
										</h4>
										<div className="space-y-2">
											<div className="flex items-center gap-2">
												<div
													className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
														config.htmlContent || config.template
															? 'bg-green-500 text-white'
															: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
													}`}
												>
													{config.htmlContent || config.template
														? '✓'
														: '!'}
												</div>
												<span
													className={`text-sm ${
														config.htmlContent || config.template
															? 'text-green-700 dark:text-green-300'
															: 'text-gray-600 dark:text-gray-400'
													}`}
												>
													Body has content
												</span>
											</div>
											<div className="flex items-center gap-2">
												<div
													className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
														config.subject
															? 'bg-green-500 text-white'
															: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
													}`}
												>
													{config.subject ? '✓' : '!'}
												</div>
												<span
													className={`text-sm ${
														config.subject
															? 'text-green-700 dark:text-green-300'
															: 'text-gray-600 dark:text-gray-400'
													}`}
												>
													Subject preview present
												</span>
											</div>
											<div className="flex items-center gap-2">
												<div
													className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
														(config.htmlContent &&
															config.htmlContent.includes('{{')) ||
														(config.subject &&
															config.subject.includes('{{'))
															? 'bg-green-500 text-white'
															: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
													}`}
												>
													{(config.htmlContent &&
														config.htmlContent.includes('{{')) ||
													(config.subject &&
														config.subject.includes('{{'))
														? '✓'
														: '!'}
												</div>
												<span
													className={`text-sm ${
														(config.htmlContent &&
															config.htmlContent.includes('{{')) ||
														(config.subject &&
															config.subject.includes('{{'))
															? 'text-green-700 dark:text-green-300'
															: 'text-gray-600 dark:text-gray-400'
													}`}
												>
													Uses at least one token
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</div>

			{/* Step 2 Checklist - Right column sticky on ≥1024px */}
			<div className="lg:hidden">
				<div className="p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
					<h4 className="mb-3 text-sm font-medium text-green-900 dark:text-green-200">
						✅ Step 2 Checklist
					</h4>
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<div
								className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
									config.htmlContent || config.template
										? 'bg-green-500 text-white'
										: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
								}`}
							>
								{config.htmlContent || config.template ? '✓' : '!'}
							</div>
							<span
								className={`text-sm ${
									config.htmlContent || config.template
										? 'text-green-700 dark:text-green-300'
										: 'text-gray-600 dark:text-gray-400'
								}`}
							>
								Body has content
							</span>
						</div>
						<div className="flex items-center gap-2">
							<div
								className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
									config.subject
										? 'bg-green-500 text-white'
										: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
								}`}
							>
								{config.subject ? '✓' : '!'}
							</div>
							<span
								className={`text-sm ${
									config.subject
										? 'text-green-700 dark:text-green-300'
										: 'text-gray-600 dark:text-gray-400'
								}`}
							>
								Subject preview present
							</span>
						</div>
						<div className="flex items-center gap-2">
							<div
								className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
									(config.htmlContent && config.htmlContent.includes('{{')) ||
									(config.subject && config.subject.includes('{{'))
										? 'bg-green-500 text-white'
										: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
								}`}
							>
								{(config.htmlContent && config.htmlContent.includes('{{')) ||
								(config.subject && config.subject.includes('{{'))
									? '✓'
									: '!'}
							</div>
							<span
								className={`text-sm ${
									(config.htmlContent && config.htmlContent.includes('{{')) ||
									(config.subject && config.subject.includes('{{'))
										? 'text-green-700 dark:text-green-300'
										: 'text-gray-600 dark:text-gray-400'
								}`}
							>
								Uses at least one token
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderStep3TokenPreview = () => {
		// Extract all tokens from the email configuration
		const extractTokens = (text: string): string[] => {
			const tokenRegex = /\{\{([^}]+)\}\}/g;
			const tokens: string[] = [];
			let match;
			while ((match = tokenRegex.exec(text)) !== null) {
				if (!tokens.includes(match[1].trim())) {
					tokens.push(match[1].trim());
				}
			}
			return tokens;
		};

		const allTokens = new Set<string>();

		// Extract tokens from all config fields
		if (config.from.name)
			extractTokens(config.from.name).forEach(token => allTokens.add(token));
		if (config.from.address)
			extractTokens(config.from.address).forEach(token => allTokens.add(token));
		if (config.subject) extractTokens(config.subject).forEach(token => allTokens.add(token));
		if (config.htmlContent)
			extractTokens(config.htmlContent).forEach(token => allTokens.add(token));
		config.to.forEach(email => extractTokens(email).forEach(token => allTokens.add(token)));

		const tokensArray = Array.from(allTokens);
		console.log('All tokens found:', tokensArray);
		console.log('Enhanced mock context:', enhancedMockContext);

		const resolvedTokens = tokensArray.map(token => {
			const resolvedValue = resolveNestedValue(enhancedMockContext, token);
			const isResolved = !!resolvedValue;
			console.log(
				`Token "${token}": resolvedValue =`,
				resolvedValue,
				'isResolved =',
				isResolved
			);

			return {
				token,
				resolved: resolvedValue || `{{${token}}}`,
				isResolved,
				usage: [] as string[],
			};
		});

		// Find where each token is used
		resolvedTokens.forEach(tokenInfo => {
			const usage: string[] = [];
			if (config.from.name && config.from.name.includes(`{{${tokenInfo.token}}}`))
				usage.push('From Name');
			if (config.from.address && config.from.address.includes(`{{${tokenInfo.token}}}`))
				usage.push('From Email');
			if (config.subject && config.subject.includes(`{{${tokenInfo.token}}}`))
				usage.push('Subject');
			if (config.template && config.template.includes(`{{${tokenInfo.token}}}`))
				usage.push('Email Body');
			config.to.forEach((email, index) => {
				if (email.includes(`{{${tokenInfo.token}}}`))
					usage.push(`To Recipient ${index + 1}`);
			});
			tokenInfo.usage = usage;
		});

		const resolvedTokensList = resolvedTokens.filter(t => t.isResolved);
		const unresolvedTokensList = resolvedTokens.filter(t => !t.isResolved);

		return (
			<div className="space-y-6">
				<div className="text-center">
					<h3 className="text-lg font-medium text-gray-900 dark:text-white">
						Token Preview
					</h3>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						Review tokens and their resolved values before sending.
					</p>
				</div>

				{tokensArray.length === 0 ? (
					<div className="py-12 text-center">
						<div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full dark:bg-gray-700">
							<span className="text-2xl">🎯</span>
						</div>
						<h4 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
							No Tokens Found
						</h4>
						<p className="mx-auto max-w-md text-gray-500 dark:text-gray-400">
							Your email doesn't use any dynamic tokens yet. Add tokens like{' '}
							<code className="px-1 bg-gray-100 rounded dark:bg-gray-700">
								{'{{firstName}}'}
							</code>{' '}
							to make your emails dynamic.
						</p>
					</div>
				) : (
					<>
						{/* Statistics with Tooltips */}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							<div className="group relative p-4 text-center bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
								<div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
									{tokensArray.length}
								</div>
								<div className="text-sm text-blue-700 dark:text-blue-300">
									Total Tokens
								</div>
								<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
									All tokens detected in this email.
									<div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
								</div>
							</div>
							<div className="group relative p-4 text-center bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
								<div className="text-2xl font-bold text-green-600 dark:text-green-400">
									{resolvedTokensList.length}
								</div>
								<div className="text-sm text-green-700 dark:text-green-300">
									Resolved
								</div>
								<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
									Tokens successfully matched to upstream data.
									<div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
								</div>
							</div>
							<div className="group relative p-4 text-center bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
								<div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
									{unresolvedTokensList.length}
								</div>
								<div className="text-sm text-yellow-700 dark:text-yellow-300">
									Unresolved
								</div>
								<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
									Tokens that won't be replaced with values.
									<div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
								</div>
							</div>
						</div>

						{/* Resolved Tokens Section */}
						{resolvedTokensList.length > 0 && (
							<div className="space-y-3">
								<div className="flex items-center gap-2">
									<span className="text-lg">✅</span>
									<h4 className="text-lg font-medium text-green-900 dark:text-green-200">
										Resolved Tokens
									</h4>
								</div>
								<div className="grid gap-3">
									{resolvedTokensList.map((tokenInfo, index) => (
										<div
											key={index}
											className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
										>
											<div className="flex justify-between items-start">
												<div className="flex-1 min-w-0">
													<div className="flex gap-2 items-center mb-2">
														<code className="px-2 py-1 font-mono text-sm text-green-700 bg-green-100 rounded dark:bg-green-900/40 dark:text-green-300">
															{`{{${tokenInfo.token}}}`}
														</code>
														<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
															✓ Resolved
														</span>
													</div>
													<div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
														<strong>Resolves to:</strong>{' '}
														<span className="font-mono">
															{typeof tokenInfo.resolved === 'object'
																? JSON.stringify(tokenInfo.resolved)
																: String(tokenInfo.resolved)}
														</span>
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">
														<strong>Used in:</strong>{' '}
														{tokenInfo.usage.length > 0
															? tokenInfo.usage.join(', ')
															: 'Not used'}
													</div>
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Unresolved Tokens Section */}
						{unresolvedTokensList.length > 0 && (
							<div className="space-y-3">
								<div className="flex items-center gap-2">
									<span className="text-lg">⚠️</span>
									<h4 className="text-lg font-medium text-yellow-900 dark:text-yellow-200">
										Unresolved Tokens
									</h4>
								</div>
								<div className="grid gap-3">
									{unresolvedTokensList.map((tokenInfo, index) => (
										<div
											key={index}
											className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
										>
											<div className="flex justify-between items-start">
												<div className="flex-1 min-w-0">
													<div className="flex gap-2 items-center mb-2">
														<code className="px-2 py-1 font-mono text-sm text-yellow-700 bg-yellow-100 rounded dark:bg-yellow-900/40 dark:text-yellow-300">
															{`{{${tokenInfo.token}}}`}
														</code>
														<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
															⚠ Unresolved
														</span>
													</div>
													<div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
														<strong>Resolves to:</strong>{' '}
														<span className="font-mono text-yellow-600 dark:text-yellow-400">
															{`{{${tokenInfo.token}}}`}
														</span>
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400">
														<strong>Used in:</strong>{' '}
														{tokenInfo.usage.length > 0
															? tokenInfo.usage.join(', ')
															: 'Not used'}
													</div>
												</div>
												<div className="ml-4">
													<button
														type="button"
														onClick={() => {
															console.log(
																'Go Fix button clicked for token:',
																tokenInfo.token
															);
															console.log(
																'Current step before:',
																currentStep
															);
															// Navigate to Step 2 to fix the token
															navigateToStep(2);
															console.log(
																'Current step after navigateToStep(2):',
																currentStep
															);
														}}
														className="px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-lg border border-yellow-300 transition-colors dark:text-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-600 hover:bg-yellow-200 dark:hover:bg-yellow-900/60"
														title="Go to Step 2 to fix this token"
													>
														Go Fix
													</button>
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Contextual Preview - Collapsible */}
						{tokensArray.length > 0 && (
							<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
								<button
									onClick={() => setShowTokenPreview(!showTokenPreview)}
									className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
								>
									<div className="flex items-center gap-2">
										<span className="text-lg">👁️</span>
										<span className="text-sm font-medium text-gray-900 dark:text-white">
											Show in Email Preview
										</span>
									</div>
									{showTokenPreview ? (
										<ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
									) : (
										<ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
									)}
								</button>

								{showTokenPreview && (
									<div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
										<div className="space-y-3">
											{/* Subject Preview */}
											{config.subject && (
												<div>
													<div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
														Subject:
													</div>
													<div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border">
														{config.subject
															.split(/(\{\{[^}]+\}\})/)
															.map((part, index) => {
																if (part.match(/\{\{[^}]+\}\}/)) {
																	const token = part.slice(2, -2);
																	const isResolved =
																		!!resolveNestedValue(
																			enhancedMockContext,
																			token
																		);
																	return (
																		<span
																			key={index}
																			className={`font-mono px-1 py-0.5 rounded ${
																				isResolved
																					? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 underline decoration-green-500'
																					: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 underline decoration-yellow-500'
																			}`}
																			title={
																				isResolved
																					? 'Resolved token'
																					: 'Unresolved token'
																			}
																		>
																			{part}
																		</span>
																	);
																}
																return part;
															})}
													</div>
												</div>
											)}

											{/* Body Preview */}
											{config.htmlContent && (
												<div>
													<div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
														Body:
													</div>
													<div className="p-3 bg-gray-50 dark:bg-gray-700 rounded border max-h-32 overflow-auto">
														{config.htmlContent
															.split(/(\{\{[^}]+\}\})/)
															.map((part, index) => {
																if (part.match(/\{\{[^}]+\}\}/)) {
																	const token = part.slice(2, -2);
																	const isResolved = !!(
																		enhancedMockContext as any
																	)[token];
																	return (
																		<span
																			key={index}
																			className={`font-mono px-1 py-0.5 rounded ${
																				isResolved
																					? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 underline decoration-green-500'
																					: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 underline decoration-yellow-500'
																			}`}
																			title={
																				isResolved
																					? 'Resolved token'
																					: 'Unresolved token'
																			}
																		>
																			{part}
																		</span>
																	);
																}
																return part;
															})}
													</div>
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						)}

						{/* Warning Banner - Revised */}
						{unresolvedTokensList.length > 0 && (
							<div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700">
								<div className="flex items-start">
									<div className="flex-shrink-0">
										<span className="text-amber-600 dark:text-amber-400">
											⚠️
										</span>
									</div>
									<div className="ml-3">
										<h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
											Unresolved Tokens
										</h4>
										<p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
											Unresolved tokens will not be replaced with data.
											They'll appear exactly as written in the final email.
										</p>
									</div>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		);
	};

	const renderStep4TestSend = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">Test Send</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Send a test email to verify your configuration works perfectly
				</p>
			</div>

			{/* Email Preview Card */}
			<div className="overflow-hidden bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-600">
				<div className="px-4 py-3 bg-gray-50 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-600">
					<h4 className="text-sm font-medium text-gray-900 dark:text-white">
						📧 Email Preview with Test Data
					</h4>
				</div>
				<div className="p-4 space-y-3">
					<div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
						<div>
							<span className="font-medium text-gray-700 dark:text-gray-300">
								From:
							</span>
							<div className="text-gray-900 dark:text-gray-100">
								{evaluateExpression(config.from.name, enhancedMockContext)} &lt;
								{evaluateExpression(config.from.address, enhancedMockContext)}&gt;
							</div>
						</div>
						<div>
							<span className="font-medium text-gray-700 dark:text-gray-300">
								To:
							</span>
							<div className="text-gray-900 dark:text-gray-100">
								{config.to
									.map(email => evaluateExpression(email, enhancedMockContext))
									.join(', ')}
							</div>
						</div>
					</div>
					<div>
						<span className="font-medium text-gray-700 dark:text-gray-300">
							Subject:
						</span>
						<div className="font-medium text-gray-900 dark:text-gray-100">
							{evaluateExpression(config.subject, enhancedMockContext)}
						</div>
					</div>
				</div>
			</div>

			{/* Test Email Form */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					🧪 Send Test Email
				</h4>
				<div className="space-y-3">
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Test Recipient Email
						</label>
						<input
							type="email"
							value={testEmail}
							onChange={e => setTestEmail(e.target.value)}
							placeholder="your-email@company.com"
							className="px-3 py-2 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
						/>
					</div>

					<div className="p-3 bg-blue-50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
						<h5 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
							What happens when you test:
						</h5>
						<div className="space-y-1 text-xs text-blue-800 dark:text-blue-300">
							<div>✓ Uses your current email template and settings</div>
							<div>✓ All tokens get replaced with mock test data</div>
							<div>✓ Sent from the configured sender address</div>
							<div>✓ Includes any configured attachments</div>
						</div>
					</div>

					<button
						onClick={sendTestEmail}
						disabled={
							isTestSending ||
							!testEmail ||
							!config.subject ||
							(!config.template && !config.htmlContent) ||
							!config.from.address
						}
						className="flex gap-2 justify-center items-center px-4 py-3 w-full text-sm font-medium text-white bg-teal-600 rounded-lg transition-colors hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
					>
						{isTestSending ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							<Send className="w-4 h-4" />
						)}
						{isTestSending ? 'Sending Test Email...' : 'Send Test Email'}
					</button>
				</div>
			</div>

			{/* Test Result */}
			{testResult && (
				<div
					className={`p-4 rounded-lg border ${
						testResult.includes('Success')
							? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
							: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
					}`}
				>
					<div className="flex gap-3 items-start">
						{testResult.includes('Success') ? (
							<div className="flex justify-center items-center w-5 h-5 bg-green-500 rounded-full flex-shrink-0 mt-0.5">
								<div className="w-2 h-2 bg-white rounded-full" />
							</div>
						) : (
							<X className="w-5 h-5 flex-shrink-0 mt-0.5" />
						)}
						<div>
							<h4 className="font-medium">
								{testResult.includes('Success')
									? '✅ Test Email Sent!'
									: '❌ Test Failed'}
							</h4>
							<p className="mt-1 text-sm">{testResult}</p>
							{testResult.includes('Success') && (
								<p className="mt-2 text-xs opacity-80">
									Check your inbox at <strong>{testEmail}</strong> to see how the
									email looks.
								</p>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Configuration Summary */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					📋 Configuration Summary
				</h4>
				<div className="grid grid-cols-1 gap-4 text-xs md:grid-cols-2">
					<div className="space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600 dark:text-gray-400">
								Template Format:
							</span>
							<span className="font-medium text-gray-900 capitalize dark:text-gray-100">
								{config.templateFormat}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600 dark:text-gray-400">Recipients:</span>
							<span className="font-medium text-gray-900 dark:text-gray-100">
								{config.to.length} configured
							</span>
						</div>
					</div>
					<div className="space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600 dark:text-gray-400">Sender:</span>
							<span className="font-medium text-gray-900 dark:text-gray-100">
								{config.from.address ? '✓ Configured' : '❌ Missing'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600 dark:text-gray-400">Content:</span>
							<span className="font-medium text-gray-900 dark:text-gray-100">
								{config.template || config.htmlContent ? '✓ Ready' : '❌ Missing'}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderComposeTab = () => (
		<div className="space-y-6">
			{/* Available Fields */}
			{availableFields.length > 0 && (
				<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
						Available Fields
					</h4>
					<div className="flex flex-wrap gap-1">
						{flattenedFields.slice(0, 10).map(field => (
							<button
								key={field.id}
								onClick={() => {
									navigator.clipboard.writeText(`{{${field.id}}}`);
								}}
								className="px-2 py-1 text-xs text-blue-700 bg-white rounded border border-blue-300 transition-colors dark:text-blue-300 dark:bg-gray-700 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
								title={`${field.description || field.label} - Click to copy {{${
									field.id
								}}}`}
							>
								{field.label}
							</button>
						))}
						{flattenedFields.length > 10 && (
							<span className="text-xs text-blue-600 dark:text-blue-400">
								+{flattenedFields.length - 10} more
							</span>
						)}
					</div>
				</div>
			)}

			{/* From & Reply-To */}
			<div className="grid grid-cols-2 gap-4">
				<div className="space-y-2">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						From Name
					</label>
					<div className="flex items-center">
						<VariablePicker
							nodeId={nodeId}
							value={config.from.name}
							onChange={value =>
								handleChange({ from: { ...config.from, name: value } })
							}
							placeholder="Sender Name or {{sender_name}}"
							className="w-full text-sm"
							ref={fromNameInputRef}
						/>
						<VariableButton
							fields={flattenedFields}
							onInsert={variable => {
								if (fromNameInputRef.current) {
									const cursorPos = fromNameInputRef.current.selectionStart || 0;
									const { newValue } = insertAtCursor(
										config.from.name,
										variable,
										cursorPos
									);
									handleChange({ from: { ...config.from, name: newValue } });
									insertTokenWithFeedback(variable, 'From Name');
								}
							}}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Tooltip content="Currently only noreply@vquip.com is supported as the From email address.">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							From Email <span className="text-red-500 dark:text-red-400">*</span>
						</label>
					</Tooltip>
					<div className="flex items-center">
						<VariablePicker
							nodeId={nodeId}
							value={config.from.address}
							onChange={value =>
								handleChange({ from: { ...config.from, address: value } })
							}
							placeholder="noreply@vquip.com (only supported address)"
							className="w-full text-sm"
							ref={fromEmailInputRef}
						/>
						<VariableButton
							fields={flattenedFields}
							onInsert={variable => {
								if (fromEmailInputRef.current) {
									const cursorPos = fromEmailInputRef.current.selectionStart || 0;
									const { newValue } = insertAtCursor(
										config.from.address,
										variable,
										cursorPos
									);
									handleChange({ from: { ...config.from, address: newValue } });
									insertTokenWithFeedback(variable, 'From Email');
								}
							}}
						/>
					</div>
				</div>
			</div>

			{/* Recipients */}
			<div className="space-y-4">
				<div className="space-y-3">
					<Tooltip content="Add static or dynamic recipients. Use tokens like {{form.email}} to send to form submitters.">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							To Recipients <span className="text-red-500 dark:text-red-400">*</span>
						</label>
					</Tooltip>
					{(config.to || []).map((email, index) => (
						<div key={index} className="space-y-2">
							<div className="flex gap-2 items-center">
								<VariablePicker
									nodeId={nodeId}
									value={email}
									onChange={value => updateRecipient('to', index, value)}
									placeholder="recipient@example.com or {{user_email}}"
									className="flex-1 min-w-0"
								/>
								<TokenPicker
									onInsert={token => {
										const currentValue = email || '';
										const newValue = currentValue
											? currentValue + token
											: token;
										updateRecipient('to', index, newValue);
										insertTokenWithFeedback(token, 'Recipient');
									}}
								/>
								<button
									onClick={() => removeRecipient('to', index)}
									className="p-2 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
								>
									<X className="w-4 h-4" />
								</button>
							</div>
							{email && email.includes('{{') && (
								<div className="p-2 ml-0 text-xs text-gray-500 bg-gray-50 rounded border dark:text-gray-400 dark:bg-gray-700">
									<strong>Preview:</strong>{' '}
									{evaluateExpression(email, enhancedMockContext)}
								</div>
							)}
						</div>
					))}
					<button
						onClick={() => addRecipient('to')}
						className="flex gap-1 items-center px-3 py-2 text-sm text-green-600 bg-green-50 rounded-md border border-green-200 transition-colors dark:text-green-400 dark:bg-green-900/30 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50"
					>
						<Plus className="w-4 h-4" />
						Add To Recipient
					</button>
				</div>
			</div>

			{/* Subject */}
			<div className="space-y-2">
				<Tooltip content="Use tokens like {{form.first_name}} or {{invoice.data.total}} to personalize the subject line.">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Subject <span className="text-red-500 dark:text-red-400">*</span>
					</label>
				</Tooltip>
				<div className="flex gap-2 items-center">
					<VariablePicker
						nodeId={nodeId}
						value={config.subject}
						onChange={value => handleChange({ subject: value })}
						placeholder="Email subject with {{variables}}"
						className="flex-1 min-w-0"
						ref={subjectInputRef}
					/>
					<TokenPicker
						onInsert={token => {
							const currentValue = config.subject || '';
							const newValue = currentValue ? currentValue + token : token;
							handleChange({ subject: newValue });
						}}
					/>
				</div>
			</div>

			{/* Template Format */}
			<div className="flex justify-between items-center">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Template Format
				</label>
				<select
					value={config.templateFormat}
					onChange={e => handleFormatChange(e.target.value as 'html' | 'plaintext')}
					className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
				>
					<option value="html">HTML</option>
					<option value="plaintext">Plain Text</option>
				</select>
			</div>

			{/* Template Mode Selection */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Email Content Type
				</label>
				<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
					<button
						type="button"
						onClick={() => {
							if (config.template) {
								handleChange({
									template: '',
									htmlContent: config.template.includes('<')
										? config.template
										: config.htmlContent || '',
								});
							}
						}}
						className={`p-2 text-left border rounded-lg transition-colors ${
							!config.template || config.htmlContent
								? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
								: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
						}`}
					>
						<div className="text-sm font-medium text-gray-900 dark:text-white">
							Custom Content
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Write your own HTML/text
						</div>
					</button>

					<button
						type="button"
						onClick={() => {
							handleChange({
								template: 'genericWorkflowTemplate',
								htmlContent: '',
							});
						}}
						className={`p-2 text-left border rounded-lg transition-colors ${
							config.template && !config.htmlContent
								? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
								: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
						}`}
					>
						<div className="text-sm font-medium text-gray-900 dark:text-white">
							Predefined Template
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Use system template
						</div>
					</button>
				</div>
			</div>

			{/* Template Content */}
			<div className="space-y-2">
				{config.template && !config.htmlContent ? (
					// Predefined Template Mode
					<>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Predefined Template{' '}
							<span className="text-red-500 dark:text-red-400">*</span>
						</label>
						<select
							value={config.template}
							onChange={e => handleChange({ template: e.target.value })}
							className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
						>
							<option value="">Select a predefined template</option>
							<option value="forgotPasswordTemplate">Forgot Password Template</option>
							<option value="bdxExportTemplate">BDX Export Template</option>
							<option value="stripeFailureTemplate">Stripe Failure Template</option>
							<option value="genericWorkflowTemplate">
								Generic Workflow Template
							</option>
						</select>
					</>
				) : (
					// Custom Content Mode
					<>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Custom Email Content{' '}
							<span className="text-red-500 dark:text-red-400">*</span>
						</label>
						<div className="flex items-center">
							<div className="overflow-hidden flex-1 rounded-lg border">
								<Editor
									key={editorKey}
									height="300px"
									language={
										config.templateFormat === 'html' ? 'html' : 'plaintext'
									}
									value={config.htmlContent || ''}
									onChange={value => handleChange({ htmlContent: value || '' })}
									options={{
										minimap: { enabled: false },
										lineNumbers: 'on',
										scrollBeyondLastLine: false,
										wordWrap: 'on',
										wrappingIndent: 'indent',
										automaticLayout: true,
										formatOnPaste: true,
									}}
									theme={editorTheme}
									onMount={editor => (editorRef.current = editor)}
								/>
							</div>
							<VariableButton
								fields={flattenedFields}
								onInsert={variable => {
									if (editorRef.current) {
										const model = editorRef.current.getModel();
										const position = editorRef.current.getPosition();
										const value = model.getValue();
										const offset = model.getOffsetAt(position);
										const newValue =
											value.slice(0, offset) + variable + value.slice(offset);
										model.setValue(newValue);
										editorRef.current.setPosition({
											...position,
											column: position.column + variable.length,
										});
										editorRef.current.focus();
									}
								}}
							/>
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Use {'{{variableName}}'} to insert dynamic content from upstream nodes
						</div>
					</>
				)}
			</div>

			{/* Attachments */}
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Attachments
					</label>
					<button
						onClick={addAttachment}
						className="flex gap-1 items-center px-2 py-1 text-xs text-green-600 rounded dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30"
					>
						<Plus className="w-3 h-3" />
						Add Attachment
					</button>
				</div>

				{(config.attachments || []).map((attachment, index) => (
					<div
						key={index}
						className="p-3 space-y-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600"
					>
						<div className="flex justify-between items-center">
							<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
								Attachment {index + 1}
							</h5>
							<button
								onClick={() => removeAttachment(index)}
								className="p-1 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1">
								<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
									File Name
								</label>
								<VariablePicker
									nodeId={nodeId}
									value={attachment.name}
									onChange={value => updateAttachment(index, 'name', value)}
									placeholder="filename.pdf or {{filename}}"
									className="w-full text-sm"
								/>
							</div>
							<div className="space-y-1">
								<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
									URL or Path
								</label>
								<VariablePicker
									nodeId={nodeId}
									value={attachment.url}
									onChange={value => updateAttachment(index, 'url', value)}
									placeholder="https://example.com/file.pdf or {{file_url}}"
									className="w-full text-sm"
								/>
							</div>
						</div>
					</div>
				))}

				{(!config.attachments || config.attachments.length === 0) && (
					<div className="py-4 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600">
						<Upload className="mx-auto mb-1 w-5 h-5 text-gray-400 dark:text-gray-500" />
						<p className="text-xs">No attachments</p>
					</div>
				)}
			</div>
		</div>
	);

	const renderPreviewTab = () => (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
					Email Preview
				</h4>
				<div className="flex gap-2 items-center">
					<button
						onClick={() => setShowRawPreview(!showRawPreview)}
						className="flex gap-1 items-center px-2 py-1 text-xs text-gray-700 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
					>
						{showRawPreview ? (
							<EyeOff className="w-3 h-3" />
						) : (
							<Eye className="w-3 h-3" />
						)}
						{showRawPreview ? 'Show Rendered' : 'Show Raw'}
					</button>
				</div>
			</div>

			<div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
				<div className="p-3 text-sm bg-gray-50 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-600">
					<div className="space-y-1 text-gray-900 dark:text-gray-100">
						<div>
							<strong>From:</strong> {config.from.name} &lt;{config.from.address}&gt;
						</div>
						<div>
							<strong>To:</strong> {config.to.join(', ')}
						</div>
						{config.cc && config.cc.length > 0 && (
							<div>
								<strong>CC:</strong> {config.cc.join(', ')}
							</div>
						)}
						{config.bcc && config.bcc.length > 0 && (
							<div>
								<strong>BCC:</strong> {config.bcc.join(', ')}
							</div>
						)}
						<div>
							<strong>Subject:</strong>{' '}
							{evaluateExpression(config.subject, enhancedMockContext)}
						</div>
					</div>
				</div>

				<div className="p-4 bg-white min-h-96 dark:bg-gray-800">
					{showRawPreview ? (
						<pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap dark:text-gray-300">
							{previewHtml}
						</pre>
					) : config.templateFormat === 'html' ? (
						<div
							className="max-w-none prose dark:prose-invert"
							dangerouslySetInnerHTML={{ __html: previewHtml }}
						/>
					) : (
						<div className="text-sm text-gray-700 whitespace-pre-wrap dark:text-gray-300">
							{previewHtml}
						</div>
					)}
				</div>
			</div>

			<div className="p-3 text-xs text-gray-600 bg-blue-50 rounded border border-blue-200 dark:text-gray-400 dark:bg-blue-900/20 dark:border-blue-800">
				<strong>Preview Note:</strong> This preview uses sample data from upstream nodes.
				The actual email will use real data when the workflow runs.
			</div>
		</div>
	);

	const renderTestTab = () => (
		<div className="space-y-6">
			{/* Test Email Configuration */}
			<div className="space-y-4">
				<div>
					<h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
						Test Email Configuration
					</h4>
					<p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
						Send a test email to verify your configuration works correctly.
					</p>
				</div>

				{/* Test Email Address */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Test Email Address
					</label>
					<input
						type="email"
						value={testEmail}
						onChange={e => setTestEmail(e.target.value)}
						placeholder="test@example.com"
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
					/>
				</div>

				{/* Send Test Button */}
				<button
					onClick={sendTestEmail}
					disabled={isTestSending || !testEmail}
					className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<Send className="w-4 h-4" />
					{isTestSending ? 'Sending...' : 'Send Test Email'}
				</button>

				{/* Test Result */}
				{showTestResult && (
					<div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-md">
						<h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
							Test Result
						</h5>
						<pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
							{testResult}
						</pre>
					</div>
				)}
			</div>
		</div>
	);

	const renderAdvancedTab = () => (
		<div className="space-y-6">
			{/* Advanced Settings */}
			<div className="space-y-4">
				<div>
					<h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
						Advanced Email Settings
					</h4>
					<p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
						Configure advanced email options and templates.
					</p>
				</div>

				{/* Template Selection */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Email Template
					</label>
					<select
						value={config.template || ''}
						onChange={e => handleChange({ template: e.target.value })}
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
					>
						<option value="">No template (use custom content)</option>
						<option value="genericWorkflowTemplate">Generic Workflow Template</option>
						<option value="notificationTemplate">Notification Template</option>
						<option value="confirmationTemplate">Confirmation Template</option>
					</select>
				</div>

				{/* Reply-To Address */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Reply-To Address
					</label>
					<input
						type="email"
						value={config.replyTo || ''}
						onChange={e => handleChange({ replyTo: e.target.value })}
						placeholder="reply@example.com"
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
					/>
				</div>
			</div>
		</div>
	);

	// Main render with error boundary
	try {
		return (
			<div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
				{/* Header */}
				<div className="flex justify-between items-center">
					<div className="flex items-center gap-3">
						<Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
						<div>
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								Email Configuration
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								Configure email settings and content
							</p>
						</div>
					</div>

				</div>

				{/* Wizard Mode */}
				{isWizard ? (
					<div className="space-y-6">
						{/* Validation Results */}
						{(isValidating || lastValidationResult) && (
							<ValidationResultDisplay
								errors={lastValidationResult?.errors || []}
								isValidating={isValidating}
								onDismiss={clearValidationResult}
							/>
						)}

						{/* Step Indicator */}
						<div className="flex items-center justify-center">
							<div className="flex items-center space-x-2">
								{[1, 2, 3, 4].map(step => (
									<div key={step} className="flex items-center">
										<div
											className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
												step <= currentStep
													? 'bg-blue-600 text-white'
													: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
											}`}
										>
											{step}
										</div>
										{step < 4 && (
											<div
												className={`w-12 h-0.5 mx-2 ${
													step < currentStep
														? 'bg-blue-600'
														: 'bg-gray-200 dark:bg-gray-700'
												}`}
											/>
										)}
									</div>
								))}
							</div>
						</div>

						{/* Step Content */}
						{currentStep === 1 && renderStep1ComposeEmail()}
						{currentStep === 2 && renderStep2WriteMessageBody()}
						{currentStep === 3 && renderStep3TokenPreview()}
						{currentStep === 4 && renderStep4TestSend()}
					</div>
				) : (
					/* Simple Mode */
					<div className="space-y-6">
						{/* Validation Results */}
						{(isValidating || lastValidationResult) && (
							<ValidationResultDisplay
								errors={lastValidationResult?.errors || []}
								isValidating={isValidating}
								onDismiss={clearValidationResult}
							/>
						)}

						{/* Validation Status Message */}
						{validationFailed && (
							<div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
								<div className="flex items-start gap-2">
									<AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0 dark:text-orange-400" />
									<div className="text-sm text-orange-800 dark:text-orange-200">
										<strong>Validation failed.</strong> You can review the
										errors above and fix them, or click "Save Changes Anyway" to
										save and exit the node.
									</div>
								</div>
							</div>
						)}

						{/* Tab Navigation */}
						<div className="border-b border-gray-200 dark:border-gray-700">
							<nav className="-mb-px flex space-x-8">
								{[
									{ id: 'compose', label: 'Compose', icon: Mail },
									{ id: 'advanced', label: 'Advanced', icon: HelpCircle },
									{ id: 'preview', label: 'Preview', icon: Eye },
									{ id: 'test', label: 'Test', icon: Send },
								].map(tab => (
									<button
										key={tab.id}
										onClick={() => setActiveTab(tab.id as any)}
										className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
											activeTab === tab.id
												? 'border-blue-500 text-blue-600 dark:text-blue-400'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
										}`}
									>
										<tab.icon className="w-4 h-4" />
										{tab.label}
									</button>
								))}
							</nav>
						</div>

						{/* Tab Content */}
						{activeTab === 'compose' && renderComposeTab()}
						{activeTab === 'advanced' && renderAdvancedTab()}
						{activeTab === 'preview' && renderPreviewTab()}
						{activeTab === 'test' && renderTestTab()}
					</div>
				)}
			</div>
		);
	} catch (error) {
		console.error('Error rendering EmailConfig:', error);
		return (
			<div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
				<div className="flex items-center gap-3">
					<Mail className="w-6 h-6 text-red-600 dark:text-red-400" />
					<div>
						<h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
							Email Configuration Error
						</h3>
						<p className="text-sm text-red-700 dark:text-red-300">
							There was an error loading the email configuration. Please try
							refreshing the page.
						</p>
					</div>
				</div>
			</div>
		);
	}
}
